import api from './api';
import { Stream } from './streams';

export interface UserProfile {
  userId: string;
  email: string;
  username: string;
  displayName?: string;
  bio?: string;
  profilePictureUrl?: string;
  role: string;
  followerCount: number;
  followingCount: number;
  streamCount: number;
  uploadCount: number;
  createdAt: number;
}

export interface UpdateProfileInput {
  username?: string;
  displayName?: string;
  bio?: string;
  profilePictureUrl?: string;
}

export interface UsernameCheckResult {
  available: boolean;
  suggestions?: string[];
}

export interface PublicUserProfile {
  userId: string;
  username: string;
  displayName?: string;
  bio?: string;
  profilePictureUrl?: string;
  role: string;
  followerCount: number;
  followingCount: number;
  streamCount: number;
  createdAt: number;
}

export interface UserSummary {
  userId: string;
  username: string;
  displayName?: string;
  profilePictureUrl?: string;
}

export interface FollowersResponse {
  followers: UserSummary[];
  nextToken?: string;
}

export interface FollowingResponse {
  following: UserSummary[];
  nextToken?: string;
}

export const userService = {
  /**
   * Get current user's profile
   */
  async getProfile(): Promise<UserProfile> {
    return api.get<UserProfile>('/user/profile');
  },

  /**
   * Update current user's profile
   */
  async updateProfile(input: UpdateProfileInput): Promise<UserProfile> {
    return api.put<UserProfile>('/user/profile', input);
  },

  /**
   * Get public profile by username
   */
  async getPublicProfile(username: string): Promise<PublicUserProfile> {
    return api.get<PublicUserProfile>(`/users/${username}`);
  },

  /**
   * Check if username is available
   */
  async checkUsernameAvailability(username: string): Promise<UsernameCheckResult> {
    return api.get<UsernameCheckResult>(`/users/check-username/${username}`);
  },

  /**
   * Get user's public uploads/streams by username
   */
  async getUserUploads(username: string): Promise<Stream[]> {
    return api.get<Stream[]>(`/users/${username}/uploads`);
  },

  /**
   * Follow a user
   */
  async followUser(username: string): Promise<void> {
    return api.post<void>(`/users/${username}/follow`, {});
  },

  /**
   * Unfollow a user
   */
  async unfollowUser(username: string): Promise<void> {
    return api.delete<void>(`/users/${username}/follow`);
  },

  /**
   * Check if current user is following a user
   */
  async checkFollowStatus(username: string): Promise<{ isFollowing: boolean }> {
    return api.get<{ isFollowing: boolean }>(`/users/${username}/follow/status`);
  },

  /**
   * Get followers for a user
   */
  async getFollowers(username: string, nextToken?: string): Promise<FollowersResponse> {
    const params = nextToken ? `?nextToken=${encodeURIComponent(nextToken)}` : '';
    return api.get<FollowersResponse>(`/users/${username}/followers${params}`);
  },

  /**
   * Get following for a user
   */
  async getFollowing(username: string, nextToken?: string): Promise<FollowingResponse> {
    const params = nextToken ? `?nextToken=${encodeURIComponent(nextToken)}` : '';
    return api.get<FollowingResponse>(`/users/${username}/following${params}`);
  },

  /**
   * Generate presigned URL for profile picture upload
   */
  async generateProfilePictureUploadUrl(fileName: string, fileType: string, fileSize: number): Promise<{
    uploadUrl: string;
    key: string;
    expiresAt: number;
  }> {
    return api.post('/user/profile-picture/upload-url', {
      fileName,
      fileType,
      fileSize
    });
  },

  /**
   * Validate uploaded profile picture
   */
  async validateProfilePicture(key: string): Promise<{
    isValid: boolean;
    profilePictureUrl?: string;
  }> {
    return api.post('/user/profile-picture/validate', { key });
  },

  /**
   * Delete profile picture
   */
  async deleteProfilePicture(): Promise<{ message: string }> {
    return api.delete('/user/profile-picture');
  },
};
